/**
 * O AVISO QUE VAI ATÉ A PESSOA.
 *
 * A tela de Conformidade já existia — com a aba de alertas como padrão. Mas você tinha que
 * IR ATÉ ELA. É o vício desta casa: o dado está lá, bonito, e ninguém é chamado. Foi assim
 * que a régua de cobrança passou meses sem ser usada, e foi assim que, no dia 14/07, o banco
 * tinha isto calado:
 *
 *   · ASO do Robson (Átomo) VENCIDO HÁ 124 DIAS. Ele está ATIVO.
 *   · NR-06 do Erik vencida há 51 dias.
 *   · 4 NR-18 estourando na mesma semana de agosto.
 *
 * ASO é o exame médico ocupacional. Alguém entrando em obra sem ele há quatro meses é
 * passivo trabalhista puro — e num acidente, quem responde é a empresa. Ninguém avisou
 * ninguém. O sistema não falhou: ele ficou CALADO, que é a mesma coisa.
 *
 * Este aviso fica FIXO no topo, em qualquer tela. Pedido do Ruddy: "todos na Átomo têm que
 * ser avisados, ATRAVÉS DO PAINEL — Marcos, Fernando, Paulo e Eduardo".
 *
 * 🔒 O ACESSO DE CADA UM É RESPEITADO POR CONSTRUÇÃO. Ele não decide nada aqui: se a
 * pessoa não pode ver aquele dado, a RLS devolve vazio e o aviso não aparece. O Fernando é
 * TST (só a área 'operacao') — ele vê. O cliente não vê NR de funcionário.
 *
 * O que JÁ VENCEU não se dispensa: só some quando for resolvido. O que está PRA VENCER
 * pode ser silenciado por uma semana — porque incomodar todo dia com o que ainda tem prazo
 * é o jeito mais rápido de treinar a pessoa a ignorar o aviso (e aí ela ignora o vencido
 * também).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { apiFetch } from "../lib/api";
import { estaAdiado, adiarUmaSemana } from "../lib/avisoConformidade";

type Item = {
  tipo: "equipe" | "cliente" | "ativo";
  documento: string;
  de: string;
  validade: string;
  dias: number;
  vencido: boolean;
  consequencia: string;
};

type Resumo = { vencidos: number; vencendo: number; itens: Item[] };

/* Adiar só vale pro que AINDA NÃO venceu — e por uma semana. A regra (e a
   chave do storage) mora em lib/avisoConformidade, porque a tela Pessoas mostra
   o MESMO aviso: se cada uma guardasse a própria chave, adiar aqui deixaria a
   outra gritando e o botão viraria mentira. */

export function AvisoConformidade() {
  const { getAccessToken, user } = useAuth();
  const [escondido, setEscondido] = useState(estaAdiado);

  const q = useQuery({
    queryKey: ["conformidade-resumo"],
    queryFn: () => apiFetch<{ data: Resumo }>("/colaboradores/resumo", getAccessToken()).then((r) => r.data),
    // O cliente não tem nada a ver com NR de funcionário da empresa.
    enabled: user?.role !== "cliente",
    staleTime: 5 * 60_000,
  });

  const d = q.data;
  if (!d || (d.vencidos === 0 && d.vencendo === 0)) return null;

  // 🔴 O QUE JÁ VENCEU NÃO SE DISPENSA. Só some quando for resolvido — é gente entrando em
  // obra sem documento, não uma notificação.
  const temVencido = d.vencidos > 0;
  if (!temVencido && escondido) return null;

  const grave = temVencido;
  const primeiro = d.itens.find((i) => i.vencido) ?? d.itens[0];

  return (
    <div
      className={
        "mb-4 rounded-lg border px-4 py-3 flex items-start gap-3 " +
        (grave
          ? "border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30"
          : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30")
      }
      role="alert"
    >
      <ShieldAlert
        className={"h-5 w-5 shrink-0 mt-0.5 " + (grave ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400")}
      />
      <div className="flex-1 min-w-0">
        <div className={"text-sm font-semibold " + (grave ? "text-rose-800 dark:text-rose-200" : "text-amber-800 dark:text-amber-200")}>
          {temVencido
            ? `${d.vencidos} documento${d.vencidos > 1 ? "s" : ""} VENCIDO${d.vencidos > 1 ? "S" : ""}`
            : `${d.vencendo} documento${d.vencendo > 1 ? "s" : ""} vencendo`}
          {temVencido && d.vencendo > 0 && (
            <span className="font-normal"> · e {d.vencendo} vencendo</span>
          )}
        </div>

        {/* A CONSEQUÊNCIA, não o documento. "NR-35 vencida" o cara ignora; "não pode subir
            na fachada, e num acidente o seguro não cobre" ele não ignora. */}
        {primeiro && (
          <div className="text-[13px] text-muted-foreground mt-0.5">
            <strong className="text-foreground">{primeiro.documento} — {primeiro.de}</strong>
            {primeiro.vencido
              ? ` venceu há ${Math.abs(primeiro.dias)} dias: `
              : ` vence em ${primeiro.dias} dias: `}
            {primeiro.consequencia}
          </div>
        )}

        {/* /colaboradores, NÃO /compliance.
            O Ruddy perguntou "o que você chama de compliance é o módulo Colaboradores?" — e
            achou um erro meu. A tela /compliance está `inativo: true` no menu (é uma tela
            MORTA), e o TST só pode abrir /colaboradores e /acesso: se ele clicasse aqui, o
            sistema REDIRECIONARIA ele de volta. O aviso chegaria e o caminho pra resolver
            estaria quebrado — o pior tipo de defeito, porque parece que funciona. */}
        <Link
          to="/colaboradores"
          className={
            "inline-block mt-2 text-xs font-medium px-3 py-1.5 rounded-md " +
            (grave
              ? "bg-rose-600 text-white hover:bg-rose-700"
              : "bg-amber-600 text-white hover:bg-amber-700")
          }
        >
          Ver e resolver
        </Link>
      </div>

      {/* Só o que AINDA NÃO venceu pode ser adiado — e por uma semana. */}
      {!temVencido && (
        <button
          onClick={() => {
            adiarUmaSemana();
            setEscondido(true);
          }}
          title="Lembrar daqui a uma semana"
          className="shrink-0 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
